import { useCallback, useRef, useState } from 'react';
import { isAtlasUnfiledId } from '../constants';

const canvasPoint = (containerRef, clientX, clientY) => {
  const el = containerRef.current;
  if (!el) return { x: clientX, y: clientY };
  const rect = el.getBoundingClientRect();
  return { x: clientX - rect.left, y: clientY - rect.top };
};

/**
 * Right-click (or alt+left) marquee select on the Atlas canvas.
 * The visible box is in screen/canvas pixels so it stays under the cursor
 * even when the world layer is scaled.
 */
export function useAtlasMarquee({ containerRef, nodesRef, screenToWorld, setSelectedIds, setCtx }) {
  const [selectionBox, setSelectionBox] = useState(null);
  const marqueeRef = useRef(null);
  const didMarqueeDragRef = useRef(false);
  const selectionBoxElRef = useRef(null);

  const startMarquee = useCallback(
    (e) => {
      e.preventDefault();
      const world = screenToWorld(e.clientX, e.clientY);
      const canvas = canvasPoint(containerRef, e.clientX, e.clientY);
      didMarqueeDragRef.current = false;
      marqueeRef.current = {
        pointerId: e.pointerId,
        start: world,
        clientStart: { x: e.clientX, y: e.clientY },
        canvasStart: canvas,
        box: { x: world.x, y: world.y, width: 0, height: 0 },
      };
      selectionBoxElRef.current = null;
      setSelectionBox({ x: canvas.x, y: canvas.y, width: 0, height: 0 });
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [containerRef, screenToWorld]
  );

  const moveMarquee = useCallback(
    (e) => {
      const m = marqueeRef.current;
      if (!m || m.pointerId !== e.pointerId) return false;
      const dx = e.clientX - m.clientStart.x;
      const dy = e.clientY - m.clientStart.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) didMarqueeDragRef.current = true;
      const world = screenToWorld(e.clientX, e.clientY);
      const canvas = canvasPoint(containerRef, e.clientX, e.clientY);
      m.box = {
        x: Math.min(m.start.x, world.x),
        y: Math.min(m.start.y, world.y),
        width: Math.abs(world.x - m.start.x),
        height: Math.abs(world.y - m.start.y),
      };
      const screenBox = {
        x: Math.min(m.canvasStart.x, canvas.x),
        y: Math.min(m.canvasStart.y, canvas.y),
        width: Math.abs(canvas.x - m.canvasStart.x),
        height: Math.abs(canvas.y - m.canvasStart.y),
      };
      if (!selectionBoxElRef.current) {
        selectionBoxElRef.current = containerRef.current?.querySelector(
          '.atlas__selection-box'
        );
      }
      const el = selectionBoxElRef.current;
      if (el) {
        el.style.left = `${screenBox.x}px`;
        el.style.top = `${screenBox.y}px`;
        el.style.width = `${screenBox.width}px`;
        el.style.height = `${screenBox.height}px`;
      }
      return true;
    },
    [containerRef, screenToWorld]
  );

  const endMarquee = useCallback(
    (e) => {
      const m = marqueeRef.current;
      if (!m || m.pointerId !== e.pointerId) return false;
      const box = m.box;
      marqueeRef.current = null;
      selectionBoxElRef.current = null;
      setSelectionBox(null);
      try {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
      } catch (_) {
        /* ignore */
      }
      if (didMarqueeDragRef.current && box && (box.width > 2 || box.height > 2)) {
        const hit = new Set();
        const boxR = box.x + box.width;
        const boxB = box.y + box.height;
        for (const n of nodesRef.current) {
          if (isAtlasUnfiledId(n.id)) continue;
          if (
            n.x < boxR &&
            n.x + n.width > box.x &&
            n.y < boxB &&
            n.y + n.height > box.y
          ) {
            hit.add(n.id);
          }
        }
        setSelectedIds((prev) => (e.shiftKey ? new Set([...prev, ...hit]) : hit));
        setCtx(null);
      }
      return true;
    },
    [nodesRef, setSelectedIds, setCtx]
  );

  return {
    selectionBox,
    marqueeRef,
    didMarqueeDragRef,
    startMarquee,
    moveMarquee,
    endMarquee,
  };
}
